/**
 * 관리자 관리의 API를 정의
 *
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-19
 */
import {
  AdmDto,
  AdmListRequestDto,
  AdmNmDto,
  AdmCreateRequestDto,
  AdmDetailResponseDto,
  AdmUpdateRequestDto,
  AdmPwdUpdateRequestDto,
} from "@/dto/AdmDto";
import instance from "./instance";
import { ListResponseDto } from "@/dto/ListDto";
import { AdmAuth, Yn } from "@/constants/code";
import { ResponseDto } from "@/dto/ResponseDto";
import { ENV } from "@/constants/env";

// 🔥 prefix 포함하여 올바른 경로 설정
const PREFIX_PATH = `${ENV.PRE_FIX}/${ENV.ADMIN_PATH}/admins`;

/**
 * 관리자 목록 조회
 * @returns {Promise<AdmDto[]>}
 */
export const findAll = async (
  search?: AdmListRequestDto
): Promise<ListResponseDto<AdmDto>> => {
  const params: {
    page: number;
    limit: number;
    keyword?: string;
    admAuth?: string;
    useYn?: string;
  } = {
    page: search?.page || 1,
    limit: search?.limit || 10,
  };

  if (search) {
    if (search.keyword && search.keyword.trim().length > 0) {
      params.keyword = search.keyword;
    }

    if (search.admAuth && search.admAuth.length > 0) {
      params.admAuth = search.admAuth
        .filter((a) => [AdmAuth.Admin, AdmAuth.Super].includes(a))
        .join(",");
    }

    if (search.useYn && search.useYn.length > 0) {
      params.useYn = search.useYn
        .filter((a) => [Yn.Y, Yn.N].includes(a))
        .join(",");
    }
  }

  console.log("PARAMS >> ", params, search);

  return await instance.get(`${PREFIX_PATH}`, {
    params,
  });
};

/**
 * 토큰으로 관리자 이름 조회
 * @returns {Promise<AdmNmDto>}
 */
export const getAdminNmByToken = async (): Promise<AdmNmDto> =>
  await instance.get(`${ENV.PRE_FIX}/${ENV.ADMIN_PATH}/auth/admin-name`);

/**
 * 관리자 생성
 * @param adm
 * @returns
 */
export const createAdmin = async (adm: AdmCreateRequestDto) =>
  await instance.post(`${PREFIX_PATH}`, adm);

/**
 * 관리자 상세 조회
 * @param id
 * @returns
 */
export const findById = async (
  id: number | string
): Promise<AdmDetailResponseDto> => await instance.get(`${PREFIX_PATH}/${id}`);

/**
 * 관리자 정보 수정
 *
 * @param id
 * @param data
 * @returns
 */
export const update = async (
  data: AdmUpdateRequestDto,
  id: string
): Promise<ResponseDto> => await instance.put(`${PREFIX_PATH}/${id}`, data);

/**
 * 관리자 삭제
 * @param id
 * @returns {Promise<ResponseDto>}
 */
export const deleteById = async (id: string): Promise<ResponseDto> =>
  await instance.delete(`${PREFIX_PATH}/${id}`);

/**
 * 관리자 비밀번호 수정
 * @param id
 * @param data
 * @returns
 */
export const changePassword = async (
  id: string,
  data: AdmPwdUpdateRequestDto
): Promise<ResponseDto> =>
  await instance.put(`${PREFIX_PATH}/${id}/password`, data);

/**
 * 슈퍼 관리자 생성
 * @param data
 * @returns
 */
export const createAdminBySuper = async (data: AdmCreateRequestDto) =>
  await instance.post(`${PREFIX_PATH}`, data);

/**
 * 도장별 통계 조회
 * @param comId 도장 ID
 * @returns 도장별 통계 데이터
 */
export const getCompanyStatistics = async (comId: number) => {
  const PREFIX_PATH = `${ENV.PRE_FIX}/${ENV.ADMIN_PATH}`;
  const apiUrl = `${PREFIX_PATH}/dashboard/stats/${comId}`;

  console.log("[API_CALL]", {
    url: apiUrl,
    method: "GET",
    comId,
    PREFIX_PATH,
    baseURL: ENV.API_URL,
  });

  const response = await instance.get(apiUrl);

  console.log("[API_RESPONSE]", {
    url: apiUrl,
    success: response.success,
    comId,
    trialCount: response.data?.trialStats?.totalCount,
    faqCount: response.data?.faqStats?.totalCount,
    qnaCount: response.data?.qnaStats?.totalCount,
  });

  if (!response.success) {
    throw new Error(response.message || "Failed to fetch company statistics");
  }

  return response.data;
};
